import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import Select from 'react-select'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import useTasks from '../../../hooks/useTasks'
import VolumeChart from './VolumeChart'
import Calendar from '../../Calendar'
import FeesChart from './FeesChart'
import GasChart from './GasChart'
import GasUsdChart from './GasUsdChart'
import SimulationsChart from './SimulationsChart'
import SlippageChart from './SlippageChart'
import ExecutionsDelayedChart from './ExecutionsDelayedChart'
import ExecutionsSucceededChart from './ExecutionsSucceededChart'
import ExecutionsRevertedChart from './ExecutionsRevertedChart'
import ExecutionsUnreachedChart from './ExecutionsUnreachedChart'
import useVolume from '../../../hooks/useVolume'
import useFees from '../../../hooks/useFees'
import useGas from '../../../hooks/useGas'
import useSlippage from '../../../hooks/useSlippage'
import useSimulations from '../../../hooks/useSimulations'

interface StatsProps {
  chainId: number
}

const Stats: React.FC<StatsProps> = ({ chainId }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const params = useParams<{ id: string }>()
  const tasks = useTasks(String(chainId), params.id)
  const [startDate, setStartDate] = useState<Date | null>(null)
  const [endDate, setEndDate] = useState<Date | null>(null)
  const [component, setComponent] = useState<boolean>(false)
  const [selectedTasks, setSelectedTasks] = useState<string[]>([])

  const handleStartDateChange = (date: Date | null) => {
    setStartDate(date)
    setComponent(false)
  }

  const handleEndDateChange = (date: Date | null) => {
    setEndDate(date)
    setComponent(false)
  }

  const generateCharts = () => {
    if (startDate && endDate) {
      const searchParams = new URLSearchParams()
      searchParams.set('startDate', startDate.toISOString())
      searchParams.set('endDate', endDate.toISOString())
      navigate(`${location.pathname}?${searchParams.toString()}`)
      setComponent(true)
    }
  }

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search)
    const startDateParam = searchParams.get('startDate')
    const endDateParam = searchParams.get('endDate')

    if (startDateParam && endDateParam) {
      setStartDate(new Date(startDateParam))
      setEndDate(new Date(endDateParam))
      setComponent(true)
    }
  }, [location.search])

  const showOptions = () => {
    if (!tasks?.data || !Array.isArray(tasks.data.tasks)) return []
    return tasks.data.tasks.map((item) => ({
      value: item.id,
      label: item.name,
    }))
  }

  const handleSelectMultiTasks = (e: unknown) => {
    if (Array.isArray(e)) {
      setSelectedTasks(e.map((s) => s.value))
    } else {
      setSelectedTasks([])
    }
  }

  return (
    <Wrap>
      <Toolbar>
        <Calendar label="From date" selectedDate={startDate} onChange={handleStartDateChange} />
        <Calendar label="To date" selectedDate={endDate} onChange={handleEndDateChange} />
        <StyledSelect
          classNamePrefix="Select"
          isMulti
          onChange={handleSelectMultiTasks}
          options={showOptions()}
          placeholder="Filter by task…"
        />
        <GenerateBtn onClick={generateCharts} disabled={!startDate || !endDate}>
          Generate Charts
        </GenerateBtn>
      </Toolbar>

      {component && (
        <ChartsGrid>
          <Charts startDate={startDate} endDate={endDate} tasks={selectedTasks} chainId={String(chainId)} />
        </ChartsGrid>
      )}
    </Wrap>
  )
}

interface ChartsProps {
  startDate: Date | null
  endDate: Date | null
  tasks: string[]
  chainId: string
}

const Charts: React.FC<ChartsProps> = ({ startDate, endDate, tasks, chainId }) => {
  const params = useParams<{ id: string }>()

  const { data: volume } = useVolume(params.id, startDate, endDate, tasks, chainId)
  const { data: fees } = useFees(params.id, startDate, endDate, tasks, chainId)
  const { data: gas } = useGas(params.id, startDate, endDate, tasks, chainId)
  const { data: slippage } = useSlippage(params.id, startDate, endDate, tasks, chainId)
  const { data: simulations } = useSimulations(params.id, startDate, endDate, tasks, chainId)

  return (
    <>
      {volume && <ChartBox><VolumeChart data={volume} /></ChartBox>}
      {fees && <ChartBox><FeesChart data={fees} /></ChartBox>}
      {gas && <ChartBox><GasChart data={gas} /></ChartBox>}
      {gas && <ChartBox><GasUsdChart data={gas} /></ChartBox>}
      {slippage && <ChartBox><SlippageChart data={slippage} /></ChartBox>}
      {simulations && <ChartBox><SimulationsChart data={simulations} /></ChartBox>}
      {simulations && <ChartBox><ExecutionsSucceededChart data={simulations} /></ChartBox>}
      {simulations && <ChartBox><ExecutionsDelayedChart data={simulations} /></ChartBox>}
      {simulations && <ChartBox><ExecutionsRevertedChart data={simulations} /></ChartBox>}
      {simulations && <ChartBox><ExecutionsUnreachedChart data={simulations} /></ChartBox>}
    </>
  )
}

export default Stats

// ─── Styles ──────────────────────────────────────────────────────────────────

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`

const GenerateBtn = styled.button`
  height: 34px;
  padding: 0 16px;
  background: rgba(111, 92, 230, 0.15);
  border: 1px solid rgba(111, 92, 230, 0.35);
  border-radius: 6px;
  color: #a89aff;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
  white-space: nowrap;

  &:hover:not(:disabled) {
    background: rgba(111, 92, 230, 0.25);
    border-color: rgba(111, 92, 230, 0.5);
  }

  &:disabled {
    opacity: 0.35;
    cursor: default;
  }
`

const StyledSelect = styled(Select)`
  min-width: 260px;

  .Select__control {
    background: #111;
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 6px;
    min-height: 34px;
    box-shadow: none;
    cursor: pointer;

    &:hover { border-color: rgba(255, 255, 255, 0.15); }
    &.Select__control--is-focused { border-color: rgba(111, 92, 230, 0.5); }
  }

  .Select__value-container { padding: 0 10px; }
  .Select__placeholder { color: #a1a1a1; font-size: 13px; }
  .Select__input-container { color: #ededed; font-size: 13px; }
  .Select__single-value { color: #ededed; font-size: 13px; }
  .Select__multi-value {
    background: rgba(111, 92, 230, 0.15);
    border: 1px solid rgba(111, 92, 230, 0.3);
    border-radius: 4px;
  }
  .Select__multi-value__label { color: #a89aff; font-size: 12px; }
  .Select__multi-value__remove {
    color: #a89aff;
    cursor: pointer;
    &:hover { background: rgba(111, 92, 230, 0.25); color: #ededed; }
  }
  .Select__indicator-separator { background: rgba(255, 255, 255, 0.08); }
  .Select__dropdown-indicator { color: #777; &:hover { color: #a0a0a0; } }
  .Select__clear-indicator { color: #777; &:hover { color: #ff4444; } }
  .Select__menu {
    background: #161616;
    border: 1px solid rgba(255, 255, 255, 0.07);
    border-radius: 6px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
    z-index: 10;
  }
  .Select__option {
    background: transparent;
    color: #a0a0a0;
    font-size: 13px;
    cursor: pointer;
    &:hover, &.Select__option--is-focused { background: rgba(255, 255, 255, 0.04); color: #ededed; }
    &.Select__option--is-selected { background: rgba(111, 92, 230, 0.15); color: #a89aff; }
  }
  .Select__no-options-message { color: #777; font-size: 13px; }
`

const ChartsGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
`

const ChartBox = styled.div`
  width: 760px;
  height: 480px;
  background: #111;
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: 8px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex-shrink: 0;

  h1, h2, h3 {
    color: #ededed;
    font-size: 14px;
    font-weight: 600;
    margin: 0;
  }
`
